import React from "react";
import { Alert, Box, Chip, Divider, Stack, Typography } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import PivotTable from "./PivotTable";

/**
 * The result tables. Shared so an unsaved preview and a saved import read
 * exactly the same.
 *
 * Order matters: the SKU tables answer "what did we sell", the per-company
 * breakdown answers "what goes to whom", which is the handover step - so it
 * comes last.
 */
const ReportTables = ({
  report,
  editing = false,
  edits = {},
  onCellChange,
  onCellReset,
}) => {
  if (!report) return null;

  const sizeColumns = report.sizeColumns ?? [];
  const editProps = { editing, edits, onCellChange, onCellReset };

  return (
    <>
      {report.summary?.excludedSizes?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <b>{report.summary.excludedRows} order(s)</b> are not shown below
          because no size chart contains their size, so this import cannot be
          saved or edited yet. Add these sizes to a size chart and parse the PDF
          again:{" "}
          <b>
            {report.summary.excludedSizes
              .map((size) => `${size.name} (${size.count})`)
              .join(", ")}
          </b>
        </Alert>
      )}

      {report.issues?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {report.issues.length} page(s) could not be read. First: page{" "}
          {report.issues[0].page} - {report.issues[0].error}
        </Alert>
      )}

      {!sizeColumns.length && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No size chart has been set up yet, so there are no columns to show.
          Create a size chart under <b>Settings &gt; Sizes &gt; Size Charts</b>.
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Mapped SKUs ({report.mapped?.length ?? 0})
      </Typography>
      <PivotTable
        rows={report.mapped ?? []}
        sizeColumns={sizeColumns}
        emptyText="No SKU from this PDF matched a product."
        {...editProps}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Unmapped SKUs ({report.unmapped?.length ?? 0})
      </Typography>
      {report.unmapped?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This import cannot be saved while any SKU is unmapped. Add these SKUs
          to the matching product or variant, then parse the PDF again.
        </Alert>
      )}
      <PivotTable
        rows={report.unmapped ?? []}
        sizeColumns={sizeColumns}
        showOrderNo
        emptyText="Every SKU in this PDF was matched."
        {...editProps}
      />

      {/* Only platforms that print the delivery company on the label have a
          breakdown to show - a Myntra picklist names none. */}
      {(report.couriers?.length ?? 0) > 0 && (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Delivery Companies ({report.couriers.length})
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            What each company is collecting. These count the parcels printed on
            the labels, so edited quantities do not change them. SKUs shown in{" "}
            <Box
              component="span"
              sx={{ color: "error.main", fontWeight: "bold" }}
            >
              red
            </Box>{" "}
            are not mapped to a product.
          </Typography>

          {report.couriers.map((courier) => (
            <Box key={courier.courier} sx={{ mb: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <LocalShippingIcon
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  {courier.courier}
                </Typography>
                <Chip size="small" label={`${courier.total} orders`} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${courier.share}%`}
                />
              </Stack>
              <PivotTable
                rows={courier.rows ?? []}
                sizeColumns={sizeColumns}
                markUnmapped
                emptyText="Nothing for this company."
              />
            </Box>
          ))}
        </>
      )}
    </>
  );
};

export default ReportTables;
